import { estimateScreenCharCapacity, requestLocation, type AutoContext } from "@/lib/context";
import { useStore } from "@/lib/store";

export function ContextPanelBody() {
  const { activeId, activeSystemPrompt, settings, updateSettings, setActiveSystemPrompt } = useStore();

  const isCustom = activeId !== null && activeSystemPrompt !== null;
  const promptValue = isCustom ? (activeSystemPrompt ?? "") : settings.defaultSystemPrompt;

  const handlePromptChange = (value: string) => {
    if (activeId !== null) {
      // any edit while in an existing chat = fork to custom
      setActiveSystemPrompt(value);
    } else {
      updateSettings({ defaultSystemPrompt: value });
    }
  };

  const handleResetToDefault = () => {
    setActiveSystemPrompt(null);
  };

  const toggleAutoContext = (key: keyof AutoContext) => {
    if (key === "locationValue") return;
    updateSettings({
      autoContext: { ...settings.autoContext, [key]: !settings.autoContext[key] },
    });
  };

  const handleLocationToggle = async () => {
    if (settings.autoContext.location) {
      updateSettings({ autoContext: { ...settings.autoContext, location: false } });
      return;
    }
    try {
      const coords = await requestLocation();
      updateSettings({ autoContext: { ...settings.autoContext, location: true, locationValue: coords } });
    } catch {
      updateSettings({ autoContext: { ...settings.autoContext, location: true } });
    }
  };

  const contextItems = [
    { key: "timestamp" as const, label: "Timestamp", display: "auto" },
    { key: "language" as const, label: "Language", display: navigator.language },
    { key: "timezone" as const, label: "Timezone", display: Intl.DateTimeFormat().resolvedOptions().timeZone },
    { key: "screen" as const, label: "Screen", display: `~${estimateScreenCharCapacity()} chars` },
  ];

  return (
    <div>
      <div className="mb-4">
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs text-muted-foreground">
            System Prompt
            <span className="ml-2 text-[10px] uppercase tracking-wide">
              {activeId === null
                ? "default (saved for new chats)"
                : isCustom
                ? "custom for this chat"
                : "using default"}
            </span>
          </label>
          {isCustom && (
            <button
              onClick={handleResetToDefault}
              className="text-[10px] text-primary hover:underline"
            >
              reset to default
            </button>
          )}
        </div>
        <textarea
          value={promptValue}
          onChange={(e) => handlePromptChange(e.target.value)}
          placeholder="You are a helpful assistant..."
          rows={4}
          className="w-full resize-none rounded-lg border bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:border-primary"
        />
      </div>

      <div className="space-y-2">
        <label className="block text-xs text-muted-foreground">Auto Context</label>
        {contextItems.map((item) => (
          <div key={item.key} className="flex items-center gap-2 text-sm">
            <button
              onClick={() => toggleAutoContext(item.key)}
              className={`size-4 shrink-0 rounded border transition-colors ${
                settings.autoContext[item.key] ? "border-primary bg-primary" : "border-muted-foreground"
              }`}
            />
            <span className="w-20 shrink-0 text-muted-foreground">{item.label}</span>
            <span className="truncate text-xs text-muted-foreground/70">{item.display}</span>
          </div>
        ))}
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={handleLocationToggle}
            className={`size-4 shrink-0 rounded border transition-colors ${
              settings.autoContext.location ? "border-primary bg-primary" : "border-muted-foreground"
            }`}
          />
          <span className="w-20 shrink-0 text-muted-foreground">Location</span>
          <span className="truncate text-xs text-muted-foreground/70">
            {settings.autoContext.locationValue || "—"}
          </span>
        </div>
      </div>
    </div>
  );
}
